import { getAppConfig } from '../config';
import type { InterStoreTransferJob } from '@/lib/types';
import type { FileDownloadResponse as InterStoreTransferFileDownloadResponse } from '@/lib/types/feature/inter-store-transfer';

// 後方互換性のため、型を再エクスポート
export type { InterStoreTransferJob } from '@/lib/types';

// 後方互換性のため、エイリアスをエクスポート
export type FileDownloadResponse = InterStoreTransferFileDownloadResponse;

/**
 * 絶対URLを構築するヘルパー関数
 */
function buildAbsoluteUrl(baseUrl: string, path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  // baseUrlが相対パス（/で始まる）の場合、現在のoriginと組み合わせる
  if (baseUrl.startsWith('/')) {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000';
    const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const pathWithSlash = path.startsWith('/') ? path : `/${path}`;
    return `${origin}${base}${pathWithSlash}`;
  }

  // baseUrlが絶対URLの場合は従来の処理
  const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const pathWithSlash = path.startsWith('/') ? path : `/${path}`;

  return `${base}${pathWithSlash}`;
}

/**
 * 店間移動効果検証ジョブ一覧を取得
 */
export async function getInterStoreTransferJobs(
  accountId: string,
  authHeaders?: Record<string, string>,
  limit?: number,
  executedBy?: string,
): Promise<InterStoreTransferJob[]> {
  const config = getAppConfig();

  if (!config.apiBaseUrl) {
    throw new Error(
      `API Base URLが設定されていません。環境設定を確認してください。現在の環境: ${config.env}`,
    );
  }

  // URLパラメータを構築
  const params = new URLSearchParams();
  if (limit !== undefined) {
    params.append('limit', limit.toString());
  }
  if (executedBy) {
    params.append('executed_by', executedBy);
  }

  let url: URL;
  try {
    const pathWithParams = `/${accountId}/inter-store-transfer-analysis/jobs${params.toString() ? `?${params.toString()}` : ''}`;
    const absoluteUrl = buildAbsoluteUrl(config.apiBaseUrl, pathWithParams);
    url = new URL(absoluteUrl);
  } catch (error) {
    throw new Error(`不正なAPI Base URL: ${config.apiBaseUrl}`);
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...authHeaders,
  };

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    throw new Error(`店間移動効果検証ジョブ一覧の取得に失敗しました: ${response.status}`);
  }

  return response.json();
}

/**
 * ファイルダウンロードURL取得
 */
export async function getInterStoreTransferJobFileDownloadUrl(
  accountId: string,
  jobId: string,
  fileType: string,
  authHeaders?: Record<string, string>,
): Promise<FileDownloadResponse> {
  const config = getAppConfig();

  if (!config.apiBaseUrl) {
    throw new Error(
      `API Base URLが設定されていません。環境設定を確認してください。現在の環境: ${config.env}`,
    );
  }

  let url: URL;
  try {
    const absoluteUrl = buildAbsoluteUrl(
      config.apiBaseUrl,
      `/${accountId}/inter-store-transfer-analysis/jobs/${jobId}/download/${fileType}`,
    );
    url = new URL(absoluteUrl);
  } catch (error) {
    throw new Error(`不正なAPI Base URL: ${config.apiBaseUrl}`);
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...authHeaders,
  };

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    throw new Error(`ファイルダウンロードURL取得に失敗しました: ${response.status}`);
  }

  return response.json();
}

/**
 * 店間移動効果検証ジョブのファイルをダウンロード
 */
export async function downloadInterStoreTransferJobFile(
  accountId: string,
  jobId: string,
  fileType: string,
  authHeaders?: Record<string, string>,
): Promise<void> {
  try {
    const downloadInfo = await getInterStoreTransferJobFileDownloadUrl(
      accountId,
      jobId,
      fileType,
      authHeaders,
    );

    // 新しいタブでダウンロードURLを開く
    const link = document.createElement('a');
    link.href = downloadInfo.download_url;
    link.download = downloadInfo.file_name;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('ファイルダウンロードエラー:', error);
    throw error;
  }
}
